if (!customElements.get("flash-sale-carousel")) {
    class FlashSaleCarousel extends HTMLElement {
        constructor() {
            super();

            this.viewport = this.querySelector("[data-flash-sale-viewport]");
            this.track = this.querySelector("[data-flash-sale-track]");
            this.items = Array.from(
                this.querySelectorAll("[data-flash-sale-slide]")
            );
            this.progress = this.querySelector("[data-flash-sale-progress]");
            this.progressThumb = this.querySelector(
                "[data-flash-sale-progress-thumb]"
            );
            this.countdown = this.querySelector("[data-flash-sale-countdown]");

            this.currentIndex = 0;
            this.frameRequested = false;
            this.isPointerDown = false;
            this.hasDragged = false;
            this.suppressClick = false;

            this.onScroll = this.requestCarouselUpdate.bind(this);
            this.onResize = this.requestCarouselUpdate.bind(this);
            this.onPointerDown = this.handlePointerDown.bind(this);
            this.onPointerMove = this.handlePointerMove.bind(this);
            this.onPointerUp = this.handlePointerUp.bind(this);
            this.onClick = this.handleClick.bind(this);
            this.onKeyDown = this.handleKeyDown.bind(this);
            this.onBlockSelect = this.handleBlockSelect.bind(this);
            this.onDragStart = (event) => event.preventDefault();
        }

        connectedCallback() {
            this.initCountdown();
            if (!this.viewport || !this.track || this.items.length === 0) return;

            this.viewport.addEventListener("scroll", this.onScroll, {
                passive: true,
            });
            this.viewport.addEventListener("pointerdown", this.onPointerDown);
            this.viewport.addEventListener("pointermove", this.onPointerMove);
            this.viewport.addEventListener("pointerup", this.onPointerUp);
            this.viewport.addEventListener("pointercancel", this.onPointerUp);
            this.viewport.addEventListener("click", this.onClick, true);
            this.viewport.addEventListener("keydown", this.onKeyDown);
            this.viewport.addEventListener("dragstart", this.onDragStart);

            if (window.Shopify?.designMode) {
                document.addEventListener(
                    "shopify:block:select",
                    this.onBlockSelect
                );
            }

            if ("ResizeObserver" in window) {
                this.resizeObserver = new ResizeObserver(this.onResize);
                this.resizeObserver.observe(this.viewport);
                if (this.progress) this.resizeObserver.observe(this.progress);
            } else {
                window.addEventListener("resize", this.onResize);
            }

            this.updateCarousel();
        }

        disconnectedCallback() {
            window.clearInterval(this.countdownTimer);
            cancelAnimationFrame(this.updateFrame);

            this.viewport?.removeEventListener("scroll", this.onScroll);
            this.viewport?.removeEventListener(
                "pointerdown",
                this.onPointerDown
            );
            this.viewport?.removeEventListener(
                "pointermove",
                this.onPointerMove
            );
            this.viewport?.removeEventListener("pointerup", this.onPointerUp);
            this.viewport?.removeEventListener(
                "pointercancel",
                this.onPointerUp
            );
            this.viewport?.removeEventListener("click", this.onClick, true);
            this.viewport?.removeEventListener("keydown", this.onKeyDown);
            this.viewport?.removeEventListener("dragstart", this.onDragStart);
            document.removeEventListener(
                "shopify:block:select",
                this.onBlockSelect
            );
            this.resizeObserver?.disconnect();
            window.removeEventListener("resize", this.onResize);
        }

        get isRtl() {
            return getComputedStyle(this.viewport).direction === "rtl";
        }

        get visibleCount() {
            const firstItem = this.items[0];
            if (!firstItem) return 1;

            const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
            return Math.max(
                1,
                Math.round(
                    (this.viewport.clientWidth + gap) /
                        (firstItem.clientWidth + gap)
                )
            );
        }

        get maxIndex() {
            return Math.max(0, this.items.length - this.visibleCount);
        }

        initCountdown() {
            if (!this.countdown) return;

            const endTime = Date.parse(this.dataset.endAt || "");
            if (Number.isNaN(endTime)) {
                this.countdown.hidden = true;
                return;
            }

            this.endTime = endTime;
            this.countdownFields = {
                days: this.querySelector("[data-flash-sale-days]"),
                hours: this.querySelector("[data-flash-sale-hours]"),
                minutes: this.querySelector("[data-flash-sale-minutes]"),
                seconds: this.querySelector("[data-flash-sale-seconds]"),
            };

            this.updateCountdown();
            if (this.endTime > Date.now()) {
                this.countdownTimer = window.setInterval(
                    () => this.updateCountdown(),
                    1000
                );
            }
        }

        updateCountdown() {
            const remaining = Math.max(0, this.endTime - Date.now());
            const days = Math.floor(remaining / 86400000);
            const hours = Math.floor((remaining % 86400000) / 3600000);
            const minutes = Math.floor((remaining % 3600000) / 60000);
            const seconds = Math.floor((remaining % 60000) / 1000);

            this.countdownFields.days.textContent = String(days).padStart(
                2,
                "0"
            );
            this.countdownFields.hours.textContent = String(hours).padStart(
                2,
                "0"
            );
            this.countdownFields.minutes.textContent = String(minutes).padStart(
                2,
                "0"
            );
            this.countdownFields.seconds.textContent = String(seconds).padStart(
                2,
                "0"
            );

            if (remaining === 0) this.handleExpiredCountdown();
        }

        handleExpiredCountdown() {
            window.clearInterval(this.countdownTimer);
            this.setAttribute("data-expired", "true");

            if (window.Shopify?.designMode) return;

            if (this.dataset.expiredBehavior === "hide_section") {
                this.hidden = true;
            } else if (this.dataset.expiredBehavior === "hide_timer") {
                this.countdown.hidden = true;
            }
        }

        requestCarouselUpdate() {
            if (this.frameRequested) return;

            this.frameRequested = true;
            this.updateFrame = requestAnimationFrame(() => {
                this.frameRequested = false;
                this.updateCarousel();
            });
        }

        updateCarousel() {
            const viewportRect = this.viewport.getBoundingClientRect();
            const viewportStart = this.isRtl
                ? viewportRect.right
                : viewportRect.left;
            let closestIndex = 0;
            let closestDistance = Number.POSITIVE_INFINITY;

            this.items.forEach((item, index) => {
                const itemRect = item.getBoundingClientRect();
                const itemStart = this.isRtl ? itemRect.right : itemRect.left;
                const distance = Math.abs(itemStart - viewportStart);

                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                }
            });

            this.currentIndex = Math.min(closestIndex, this.maxIndex);
            const hasOverflow =
                this.viewport.scrollWidth > this.viewport.clientWidth + 1;
            this.classList.toggle("is-overflowing", hasOverflow);
            this.updateProgress(hasOverflow);
        }

        updateProgress(hasOverflow) {
            if (!this.progress || !this.progressThumb) return;

            this.progress.hidden = !hasOverflow;
            if (!hasOverflow) return;

            const progressWidth = this.progress.clientWidth;
            const thumbWidth = Math.max(
                36,
                progressWidth * Math.min(1, this.visibleCount / this.items.length)
            );
            const availableTravel = Math.max(0, progressWidth - thumbWidth);
            const position =
                this.maxIndex === 0
                    ? 0
                    : availableTravel * (this.currentIndex / this.maxIndex);

            this.progressThumb.style.inlineSize = `${thumbWidth}px`;
            this.progressThumb.style.insetInlineStart = `${position}px`;
        }

        handlePointerDown(event) {
            if (event.pointerType !== "mouse" || event.button !== 0) return;

            this.isPointerDown = true;
            this.hasDragged = false;
            this.startX = event.clientX;
            this.startScrollLeft = this.viewport.scrollLeft;
            this.pointerIsRtl = this.isRtl;
        }

        handlePointerMove(event) {
            if (!this.isPointerDown) return;

            const distance = event.clientX - this.startX;
            if (!this.hasDragged && Math.abs(distance) < 5) return;

            this.hasDragged = true;
            this.viewport.setPointerCapture(event.pointerId);
            this.classList.add("is-dragging");
            event.preventDefault();
            this.viewport.scrollLeft =
                this.startScrollLeft +
                (this.pointerIsRtl ? distance : -distance);
        }

        handlePointerUp(event) {
            if (event.pointerType !== "mouse" || !this.isPointerDown) return;

            this.isPointerDown = false;
            this.suppressClick = this.hasDragged;
            this.classList.remove("is-dragging");

            if (this.viewport.hasPointerCapture(event.pointerId)) {
                this.viewport.releasePointerCapture(event.pointerId);
            }

            this.updateCarousel();
            window.setTimeout(() => {
                this.suppressClick = false;
            }, 100);
        }

        handleClick(event) {
            if (!this.suppressClick) return;

            event.preventDefault();
            event.stopPropagation();
        }

        handleKeyDown(event) {
            if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

            event.preventDefault();
            const nextKey = this.isRtl ? "ArrowLeft" : "ArrowRight";
            const step = event.key === nextKey ? 1 : -1;
            this.scrollToIndex(
                Math.min(this.maxIndex, Math.max(0, this.currentIndex + step))
            );
        }

        handleBlockSelect(event) {
            const item = event.target.closest?.("[data-flash-sale-slide]");
            if (!item || !this.contains(item)) return;

            const index = this.items.indexOf(item);
            if (index >= 0) this.scrollToIndex(Math.min(index, this.maxIndex));
        }

        scrollToIndex(index) {
            const item = this.items[index];
            if (!item) return;

            item.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "start",
            });
        }
    }

    customElements.define("flash-sale-carousel", FlashSaleCarousel);
}
